/**
 * bulkAction/BulkPrefillModal.jsx
 *
 * Prefill selected KPI questions from the previous financial year.
 *
 * Props:
 *   show, onHide, selectedIds, financialYears, selectedFinancialYearId, onSuccess
 */
import { useState, useEffect } from "react";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config.json";
import { S, BigSpin } from "./Shared";

const BulkPrefillModal = ({
  show,
  onHide,
  selectedIds = [],
  financialYears = [],
  selectedFinancialYearId,
  onSuccess,
}) => {
  const [currentFY,  setCurrentFY]  = useState(null);
  const [previousFY, setPreviousFY] = useState(null);
  const [allowAll,   setAllowAll]   = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [error,      setError]      = useState("");
  const [success,    setSuccess]    = useState("");

  /* ── resolve current + previous FY on open ── */
  useEffect(() => {
    if (!show || !financialYears.length) return;
    setAllowAll(false);
    setError("");
    setSuccess("");
    setConfirming(false);

    const idx = financialYears.findIndex(
      (y) => String(y.id) === String(selectedFinancialYearId)
    );
    if (idx >= 0) {
      setCurrentFY(financialYears[idx]);
      /* previous year = next item in array (assumes desc order like Qualitative) */
      setPreviousFY(
        idx < financialYears.length - 1 ? financialYears[idx + 1] : null
      );
    } else {
      setCurrentFY(financialYears[0] || null);
      setPreviousFY(financialYears[1] || null);
    }
  }, [show, financialYears, selectedFinancialYearId]);

  const handlePrefill = async () => {
    setPrefilling(true);
    setError("");
    try {
      const payload = {
        previousFinancialYearId: Number(previousFY.id),
        currentFinancialYearId:  Number(currentFY.id),
        overwriteAllTypes:       allowAll,
      };
      /* only send questionIds if the user had specific KPIs selected */
      if (selectedIds.length > 0) payload.questionIds = selectedIds;

      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}prefillReportingFromPreviousYear`,
        {},
        payload,
        "POST"
      );
      if (isSuccess) {
        const count = data?.overwrittenCount || data?.count || 0;
        setSuccess(
          count > 0
            ? `✅ ${count} question(s) prefilled successfully.`
            : "✅ Data prefilled successfully."
        );
        onSuccess?.();
        setTimeout(() => {
          setConfirming(false);
          onHide();
        }, 2000);
      } else {
        setError(data?.message || "Prefill failed. Please try again.");
      }
    } catch {
      setError("Server error occurred.");
    } finally {
      setPrefilling(false);
    }
  };

  if (!show) return null;

  /* ── FY card sub-component ── */
  const FYCard = ({ label, fy }) => (
    <div
      style={{
        flex: 1,
        background: "#f8fafc",
        border: "1.5px solid #e2e8f0",
        borderRadius: 8,
        padding: "9px 13px",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "#94a3b8",
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
        {fy?.financial_year_value || "—"}
      </div>
    </div>
  );

  return (
    <>
      {/* ── Main Modal ── */}
      <div style={S.overlay}>
        <div style={{ ...S.modal, maxWidth: 460 }}>
          <div style={S.header}>
            <span style={S.title}>Prefill from Previous Year</span>
            {!prefilling && (
              <button onClick={onHide} style={S.closeBtn}>✕</button>
            )}
          </div>

          <div style={S.body}>
            {error   && <div style={S.errBox}>{error}</div>}
            {success && <div style={S.successBox}>{success}</div>}

            {!previousFY ? (
              <div style={S.warnBox}>
                ⚠️ No previous financial year available for{" "}
                <b>{currentFY?.financial_year_value}</b>. Cannot prefill.
              </div>
            ) : (
              <>
                {/* FY arrows display */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 18,
                  }}
                >
                  <FYCard label="Copy From" fy={previousFY} />
                  <span style={{ fontSize: 20, color: "#93c5fd", flexShrink: 0 }}>
                    →
                  </span>
                  <FYCard label="Copy To" fy={currentFY} />
                </div>

                {/* Allow all question types */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 9,
                    cursor: "pointer",
                    marginBottom: 14,
                    userSelect: "none",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allowAll}
                    onChange={(e) => setAllowAll(e.target.checked)}
                    style={{ marginTop: 3, flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 13, color: "#334155" }}>
                    <b>Allow all question types</b>
                    <br />
                    <span style={{ fontSize: 11.5, color: "#64748b" }}>
                      Quantitative trend questions are skipped by default.
                      Enable to include them.
                    </span>
                  </span>
                </label>

                {/* Scope info */}
                {selectedIds.length > 0 ? (
                  <div style={S.infoBox}>
                    <b>{selectedIds.length}</b> KPI(s) selected — only these
                    will be prefilled.
                  </div>
                ) : (
                  <div style={S.warnBox}>
                    No specific questions selected —{" "}
                    <b>all questions</b> will be prefilled.
                  </div>
                )}
              </>
            )}
          </div>

          <div style={S.footer}>
            <button
              onClick={onHide}
              disabled={prefilling}
              style={S.btnSecondary}
            >
              Close
            </button>
            {previousFY && (
              <button
                onClick={() => setConfirming(true)}
                disabled={prefilling}
                style={S.btnPrimary}
              >
                Confirm Prefill
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Confirm sub-modal ── */}
      {confirming && (
        <div style={{ ...S.overlay, zIndex: 3100 }}>
          <div style={{ ...S.modal, maxWidth: 400 }}>
            <div style={{ ...S.header, borderBottomColor: "#fca5a5" }}>
              <span style={{ ...S.title, color: "#dc2626" }}>
                ⚠️ Are you sure?
              </span>
            </div>

            <div style={S.body}>
              {prefilling ? (
                <BigSpin />
              ) : (
                <div style={S.warnBox}>
                  This will <b>overwrite existing data</b> for{" "}
                  <b>{currentFY?.financial_year_value}</b> and cannot be
                  undone.
                </div>
              )}
            </div>

            {!prefilling && (
              <div style={S.footer}>
                <button
                  onClick={() => setConfirming(false)}
                  style={S.btnSecondary}
                >
                  No, Cancel
                </button>
                <button onClick={handlePrefill} style={S.btnDanger}>
                  Yes, Proceed
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BulkPrefillModal;